import { Feature, Geometry, LineString } from "geojson";
import { wzdxFeed, RoadEvent } from "./types";

type TypeOfWork = wzdxFeed['features'][0]['properties']['types_of_work'][0];
type CoreDetails = wzdxFeed['features'][0]['properties']['core_details'];

interface MergedCoreDetails {
    [key: string]: string[];
}

type DateFormatOptions = Intl.DateTimeFormatOptions;

export default class FeatureMerger {
    private static readonly DEFAULT_LOCALE = 'en-US';
    private static readonly DEFAULT_DATE_FORMAT: DateFormatOptions = {
        weekday: 'long',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    };

    static mergeFeatures(feed: wzdxFeed): wzdxFeed {
        const mergedFeatures: Feature<Geometry, RoadEvent>[] = [];
        const checkedFeatures = new Array(feed.features.length).fill(false);

        feed.features.forEach((feature, i) => {
            if (checkedFeatures[i]) return;

            const matchedProperties: RoadEvent[] = [feature.properties];
            feed.features.forEach((otherFeature, j) => {
                if (i !== j && !checkedFeatures[j] &&
                    this.areGeometriesEqual(feature.geometry, otherFeature.geometry)) {
                    matchedProperties.push(otherFeature.properties);
                    checkedFeatures[j] = true;
                }
            });

            mergedFeatures.push({
                ...feature,
                id: `merged-${i}`,
                properties: this.mergeRoadEvents(matchedProperties)
            });

            checkedFeatures[i] = true;
        });

        return { ...feed, features: mergedFeatures };
    }

    private static areGeometriesEqual(g1: Geometry, g2: Geometry): boolean {
        if (g1.type !== 'LineString' || g2.type !== 'LineString') {
            return false;
        }

        const coords1 = (g1 as LineString).coordinates;
        const coords2 = (g2 as LineString).coordinates;

        if (coords1.length !== coords2.length) {
            return false;
        }

        // Check both forward and reverse order
        return this.compareCoordinates(coords1, coords2) ||
            this.compareCoordinates(coords1, [...coords2].reverse());
    }

    private static compareCoordinates(coords1: number[][], coords2: number[][]): boolean {
        return coords1.every((coord, i) =>
            coord[0] === coords2[i][0] && coord[1] === coords2[i][1]
        );
    }

    private static formatDate(dateString: string, locale = this.DEFAULT_LOCALE,
        options: DateFormatOptions = this.DEFAULT_DATE_FORMAT): string {
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString(locale, options);
    }

    private static toTitleCase(str: string, delimiter = ' '): string {
        const sanitizedStr = str.trim().replace(new RegExp(`${delimiter}+`, 'g'), delimiter);
        return sanitizedStr
            .toLowerCase()
            .split(delimiter)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    private static formatArray(input: string[], fieldName?: string): string {
        if (!input.length) return '';

        const processValue = (value: string): string => {
            if (!fieldName) return value;

            if (fieldName.endsWith('_date')) {
                return this.formatDate(value);
            }
            if (fieldName === 'vehicle_impact') {
                return this.toTitleCase(value, '-');
            }
            if (['event_type', 'direction'].includes(fieldName)) {
                return this.toTitleCase(value);
            }
            return value;
        };

        return Array.from(new Set(input.map(processValue))).join(' / ');
    }

    private static mergeRoadEvents(roadEvents: RoadEvent[]): RoadEvent {
        if (!roadEvents.length) {
            throw new Error('Cannot merge empty road events array');
        }

        const baseEvent = roadEvents[0];
        const mergedEvent: { [key: string]: any } = {};

        Object.entries(baseEvent).forEach(([key, value]) => {
            if (key === 'types_of_work') {
                mergedEvent[key] = this.mergeTypesOfWork(roadEvents);
            } else if (key === 'core_details') {
                mergedEvent[key] = this.mergeCoreDetails(roadEvents);
            } else if (typeof value === 'string' || Array.isArray(value)) {
                mergedEvent[key] = this.formatArray(
                    roadEvents.map(event => (event as any)[key]).flat(),
                    key
                );
            } else {
                mergedEvent[key] = value;
            }
        });

        return mergedEvent as RoadEvent;
    }

    /// Merges `types_of_work`, e.g., "surface-work"
    private static mergeTypesOfWork(roadEvents: RoadEvent[]): string {
        const typesOfWork = roadEvents.flatMap(event => {
            if(event.types_of_work) {
                event.types_of_work.map((work: TypeOfWork) => work.type_name)
            }
        });

        return this.formatArray(typesOfWork, 'types_of_work');
    }

    private static mergeCoreDetails(roadEvents: RoadEvent[]): CoreDetails {
        const mergedDetails: MergedCoreDetails = {};

        roadEvents.forEach(event => {
            Object.entries(event.core_details).forEach(([detail, value]) => {
                mergedDetails[detail] = mergedDetails[detail] || [];
                const values = Array.isArray(value) ? value : [value as string];
                mergedDetails[detail].push(...values);
            });
        });

        return Object.entries(mergedDetails).reduce((acc, [key, values]) => ({
            ...acc,
            [key]: this.formatArray(values, key)
        }), {} as CoreDetails);
    }
}